import startCase from 'lodash/startCase';

import { TprmCredentialType } from '@evidentid/tprm-portal-lib/models/common';
import {
    OpenVerificationRequirement,
    OpenVerificationSubmissionStatus,
    TprmRequirementModel,
} from '@evidentid/tprm-portal-lib/models/entity-details';
import { VerificationRequestType } from '@evidentid/tprm-portal-lib/models/notification-configuration';

export interface AwaitingRequirement {
    name: string;
    // Why it was requested; forcing treats each reason differently.
    verificationType: OpenVerificationRequirement['verificationType'];
}

export interface ForceSubmissionRequest {
    verificationId: string;
    receivedCount: number;
    awaiting: AwaitingRequirement[];
}

export function isRequirementReceived(requirement: OpenVerificationRequirement): boolean {
    return requirement.submissionStatus === OpenVerificationSubmissionStatus.ALREADY_SUBMITTED;
}

/** A missing first or expiring request is always asked for again. An on-demand one usually isn't: its coverage on
 * file is kept, so it stays compliant. */
export function leadsToNewRequest(awaiting: AwaitingRequirement[]): boolean {
    return awaiting.some((requirement) => requirement.verificationType === VerificationRequestType.new
        || requirement.verificationType === VerificationRequestType.expiring);
}

/** Forcing makes a missing follow-up requirement exhausted. No new request goes out for it unless something else
 * still needs requesting. */
export function isExhaustedByForce(awaiting: AwaitingRequirement[]): boolean {
    return !leadsToNewRequest(awaiting)
        && awaiting.some((requirement) => requirement.verificationType === VerificationRequestType.nonCompliant);
}

/** Names a requirement the same way its card on the Compliance tab does. */
export function getOpenVerificationRequirementName(
    requirement: OpenVerificationRequirement,
    requirementModels: TprmRequirementModel[],
    credentialTypes: TprmCredentialType[],
): string {
    const model = requirementModels.find((candidate) => candidate.coverageType === requirement.coverageType);
    const label = model?.label || startCase(requirement.coverageType.toLowerCase());
    const credential = requirement.instanceId
        ? credentialTypes.find((credentialType) => credentialType.id === requirement.instanceId)
        : null;
    return credential ? `${label} - ${credential.name} - ${credential.issuingAuthority}` : label;
}
